import { DEG, Vec3 } from '../core/math';
import { emptyCommand, type Ship, type ShipCommand } from '../entities';

/** Entrées joueur indépendantes du périphérique, échantillonnées une fois par tick. */
export interface PlayerInput {
  /** Poussée −1..1 : x = droite, y = haut, z = avant. */
  thrust: { x: number; y: number; z: number };
  /** Delta souris accumulé depuis le tick précédent, en degrés (sensibilité appliquée). + = droite / nez haut. */
  mouseYawDeg: number;
  mousePitchDeg: number;
  /** Regard analogique −1..1 (manette). */
  lookYaw: number;
  lookPitch: number;
  /** Roulis −1..1, + = droite. */
  roll: number;
  boost: boolean;
  brake: boolean;
  firePrimary: boolean;
  fireSecondary: boolean;
  skipIntermission: boolean;
  /** Module de saut : appui = charger vers la zone suivante, nouvel appui = annuler. */
  jump: boolean;
  /** Module de saut : changer de destination. */
  jumpCycle: boolean;
  /** Accélérateur : réglage tenu −1..1 (Z/S), crans de molette, coupure des gaz. */
  throttleAxis: number;
  throttleStep: number;
  throttleZero: boolean;
}

export const emptyInput = (): PlayerInput => ({
  thrust: { x: 0, y: 0, z: 0 },
  mouseYawDeg: 0,
  mousePitchDeg: 0,
  lookYaw: 0,
  lookPitch: 0,
  roll: 0,
  boost: false,
  brake: false,
  firePrimary: false,
  fireSecondary: false,
  skipIntermission: false,
  jump: false,
  jumpCycle: false,
  throttleAxis: 0,
  throttleStep: 0,
  throttleZero: false,
});

export interface ThrottleSettings {
  rate: number;
  wheelStep: number;
  reverseLimit: number;
}

/**
 * « direct » : la souris tourne le vaisseau sans plafond angulaire (comportement Unreal).
 * « capped » : la souris alimente une réserve vidée au taux maximal du profil, comme l'IA (plan W02).
 * « joystick » : manche virtuel ; la souris déplace un curseur, l'écart au centre fixe le taux de rotation
 *   (sensation de pilotage plutôt que de visée à la première personne).
 */
export type MouseMode = 'direct' | 'capped' | 'joystick';

export interface JoystickSettings {
  rangeDeg: number;
  deadZone: number;
  exponent: number;
  /** Part du taux de virage maximal atteinte à pleine déviation (la manette garde 100 %). */
  maxRateScale: number;
  /** Retour au centre (par seconde, exponentiel) quand la souris ne bouge plus depuis `recenterDelay`. */
  recenterRate: number;
  recenterDelay: number;
}

export class PlayerPilot {
  private yawBuffer = 0;
  private pitchBuffer = 0;
  /** Position du manche virtuel (−1..1), lue par le HUD. x = droite, y = haut. */
  readonly stick = { x: 0, y: 0 };
  private stickIdle = 0;
  /** Accélérateur persistant (−1..1), lu par le HUD. */
  throttle = 0;
  /** Cran à 0 : franchir le neutre demande de relâcher puis réappuyer. */
  private throttleDetent = false;

  constructor(
    public mouseMode: MouseMode,
    private yawScale: number,
    private pitchScale: number,
    private readonly joystick: JoystickSettings,
    private readonly throttleSettings: ThrottleSettings,
  ) {}

  /** Changement d'appareil ou d'équipement à quai. */
  setScales(yawScale: number, pitchScale: number): void {
    this.yawScale = yawScale;
    this.pitchScale = pitchScale;
  }

  setThrottle(value: number): void {
    this.throttle = Math.max(this.throttleSettings.reverseLimit, Math.min(1, value));
    this.throttleDetent = false;
  }

  /** Met à jour l'accélérateur et retourne la poussée avant à appliquer. */
  private updateThrottle(input: PlayerInput, dt: number): number {
    const t = this.throttleSettings;
    if (input.throttleZero) this.setThrottle(0);
    if (input.throttleStep !== 0) this.setThrottle(this.throttle + input.throttleStep * t.wheelStep);
    if (input.throttleAxis === 0) {
      this.throttleDetent = false;
    } else if (!this.throttleDetent) {
      const before = this.throttle;
      let next = Math.max(t.reverseLimit, Math.min(1, before + input.throttleAxis * t.rate * dt));
      // Arrêt au neutre en traversant 0.
      if ((before > 0 && next <= 0) || (before < 0 && next >= 0)) {
        next = 0;
        this.throttleDetent = true;
      }
      this.throttle = next;
    }
    return this.throttle;
  }

  resetStick(): void {
    this.stick.x = 0;
    this.stick.y = 0;
  }

  command(ship: Ship, input: PlayerInput, dt: number): ShipCommand {
    const cmd = emptyCommand();
    const rates = ship.motor.maxRates;
    // Poussée avant : commande directe (manette, mode « touche tenue », pilote auto) sinon accélérateur.
    const throttle = this.updateThrottle(input, dt);
    const forward = Math.abs(input.thrust.z) > 0.01 ? input.thrust.z : throttle;
    cmd.thrust.set(clampUnit(input.thrust.x), clampUnit(input.thrust.y), clampUnit(forward));
    cmd.boost = input.boost;
    cmd.brake = input.brake;

    // Manette et roulis : taux angulaires dans les limites du profil.
    const turn = new Vec3(clampUnit(input.lookPitch) * rates.x, -clampUnit(input.lookYaw) * rates.y, -clampUnit(input.roll) * rates.z);

    const mouseYaw = input.mouseYawDeg * this.yawScale * DEG;
    const mousePitch = input.mousePitchDeg * this.pitchScale * DEG;
    if (this.mouseMode === 'joystick') {
      const j = this.joystick;
      const moved = input.mouseYawDeg !== 0 || input.mousePitchDeg !== 0;
      this.stickIdle = moved ? 0 : this.stickIdle + dt;
      if (this.stickIdle > j.recenterDelay) {
        const decay = Math.exp(-j.recenterRate * dt);
        this.stick.x *= decay;
        this.stick.y *= decay;
      }
      this.stick.x += input.mouseYawDeg / j.rangeDeg;
      this.stick.y += input.mousePitchDeg / j.rangeDeg;
      const length = Math.hypot(this.stick.x, this.stick.y);
      if (length > 1) {
        this.stick.x /= length;
        this.stick.y /= length;
      }
      const shaped = (v: number) => {
        const a = Math.abs(v);
        if (a <= j.deadZone) return 0;
        return Math.sign(v) * Math.pow((a - j.deadZone) / (1 - j.deadZone), j.exponent);
      };
      turn.x += shaped(this.stick.y) * rates.x * j.maxRateScale;
      turn.y += -shaped(this.stick.x) * rates.y * j.maxRateScale;
    } else if (this.mouseMode === 'direct') {
      cmd.turnImpulse.set(mousePitch, -mouseYaw, 0);
    } else {
      // Réserve bornée à 0,35 s de rotation maximale pour éviter une longue traîne.
      this.yawBuffer = clampAbs(this.yawBuffer + mouseYaw, rates.y * 0.35);
      this.pitchBuffer = clampAbs(this.pitchBuffer + mousePitch, rates.x * 0.35);
      const yawStep = clampAbs(this.yawBuffer, rates.y * dt);
      const pitchStep = clampAbs(this.pitchBuffer, rates.x * dt);
      this.yawBuffer -= yawStep;
      this.pitchBuffer -= pitchStep;
      turn.x += pitchStep / dt;
      turn.y += -yawStep / dt;
    }
    cmd.turnRate = turn;
    return cmd;
  }
}

const clampUnit = (v: number) => Math.max(-1, Math.min(1, v));
const clampAbs = (v: number, max: number) => Math.max(-max, Math.min(max, v));
